import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { ActivityIndicator, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as z from "zod";

import { Button, Form, FormField, FormInput } from "@/components/ui";
import { supabase } from "@/lib/supabase";
import Toast from "react-native-toast-message";
import { KeyboardAwareScrollView, KeyboardToolbar } from "react-native-keyboard-controller";
import { useSupabase } from "@/hooks/useSupabase";

const formSchema = z
  .object({
    password: z
      .string()
      .min(8, "Please enter at least 8 characters.")
      .max(64, "Please enter fewer than 64 characters.")
      .regex(
        /^(?=.*[a-z])/,
        "Your password must have at least one lowercase letter.",
      )
      .regex(
        /^(?=.*[A-Z])/,
        "Your password must have at least one uppercase letter.",
      ),
    confirmPassword: z.string().min(8, "Please enter at least 8 characters."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Your passwords do not match.",
    path: ["confirmPassword"],
  });

export default function ResetPassword() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { isDeepLink } = useSupabase();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  async function onSubmit(formData: z.infer<typeof formSchema>) {
    const { data } = await supabase.auth.updateUser({
      password: formData.password
    })
    if (data) {
      // OneSignal external_id is set by AuthProvider whenever `user` changes,
      // so no per-screen OneSignal.login() call is needed here.
      router.replace("/(app)")
      Toast.show({
        type: 'success',
        text1: 'Password reset successfully',
      });
    }
  }

  // The reset-password screen should ONLY be shown when arriving via a Supabase
  // password-reset deep link. expo-router restores the last visited route on
  // cold start, which can briefly land users back here. Hide the contents with
  // opacity (NOT conditional rendering — that destabilises the layout tree
  // and crashes Android via the same Yoga bug class as display:'none', see
  // facebook/react-native#52349). The AuthProvider redirect routes the user
  // away within a frame; until then they see a blank white screen.
  return (
    <View
      style={{ flex: 1, backgroundColor: '#ffffff', opacity: isDeepLink ? 1 : 0 }}
      pointerEvents={isDeepLink ? 'auto' : 'none'}
    >
      <KeyboardAwareScrollView
        bottomOffset={62}
        className="flex-1 bg-background p-4"
        contentContainerStyle={{
          gap: 20,
          paddingTop: 40,
          paddingBottom: 20 + insets.bottom,
        }}
      >
        <View className="flex-1">
          <Text className="text-4xl text-foreground font-extrabold tracking-tight lg:text-5xl self-start mb-5">
            Reset Password
          </Text>
          <Form {...form}>
            <View className="gap-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormInput
                    label="Password"
                    placeholder="Password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormInput
                    label="Confirm Password"
                    placeholder="Confirm password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry
                    {...field}
                  />
                )}
              />
            </View>
          </Form>
        </View>
        <View className="gap-y-4 android:pb-4">
          <Button
            size="default"
            variant="crew"
            className="w-full"
            onPress={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" />
            ) : (
              "Reset Password"
            )}
          </Button>
        </View>
      </KeyboardAwareScrollView>
      <KeyboardToolbar />
    </View>
  );
}